/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState, useEffect, useMemo } from 'react';

interface IBGEMunicipio {
  id: number;
  nome: string;
  microrregiao?: {
    mesorregiao?: {
      UF?: {
        sigla?: string;
      };
    };
  } | null;
  'regiao-imediata'?: {
    'regiao-intermediaria'?: {
      UF?: {
        sigla?: string;
      };
    };
  } | null;
}

export interface CityOption {
  id: number;
  name: string;
  uf: string;
  label: string;
  normalized: string;
}

const IBGE_URL = 'https://servicodados.ibge.gov.br/api/v1/localidades/municipios?orderBy=nome';

let cachedCities: CityOption[] | null = null;
let fetchPromise: Promise<CityOption[]> | null = null;

/**
 * Strips diacritics and lowercases a string for accent-insensitive matching.
 *
 * @param str - The input string.
 * @returns The normalized comparison key.
 */
function normalize(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * Extracts the state (UF) abbreviation from an IBGE municipality record.
 *
 * @param m - The IBGE municipality payload.
 * @returns The UF abbreviation, or an empty string when absent.
 */
function extractUF(m: IBGEMunicipio): string {
  return m.microrregiao?.mesorregiao?.UF?.sigla
    ?? m['regiao-imediata']?.['regiao-intermediaria']?.UF?.sigla
    ?? '';
}

/**
 * Fetches the Brazilian municipality list from IBGE, cached across callers.
 *
 * @returns A promise resolving to the mapped city options (empty on error).
 */
function fetchCities(): Promise<CityOption[]> {
  if (cachedCities) return Promise.resolve(cachedCities);
  if (fetchPromise) return fetchPromise;

  fetchPromise = fetch(IBGE_URL)
    .then((res) => {
      if (!res.ok) throw new Error(`IBGE API error: ${res.status}`);
      return res.json();
    })
    .then((data: IBGEMunicipio[]) => {
      cachedCities = data
        .map((m) => {
          const uf = extractUF(m);
          return {
            id: m.id,
            name: m.nome,
            uf,
            label: uf ? `${m.nome} - ${uf}` : m.nome,
            normalized: normalize(m.nome),
          };
        })
        .filter((c) => c.name.length > 0);
      return cachedCities;
    })
    .catch((err) => {
      console.error('[useIBGECities] Failed to fetch cities:', err);
      fetchPromise = null;
      return [] as CityOption[];
    });

  return fetchPromise;
}

/**
 * Clears the module-level city cache so tests start from a clean state.
 *
 * @returns Nothing.
 */
export function __resetIBGECitiesCacheForTests(): void {
  cachedCities = null;
  fetchPromise = null;
}

/**
 * Provides debounced, accent-insensitive city suggestions from IBGE data.
 *
 * @param query - The user's partial city name input.
 * @returns The matching suggestions and the initial loading flag.
 */
export function useIBGECities(query: string) {
  const [cities, setCities] = useState<CityOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    fetchCities().then((all) => {
      if (!mounted) return;
      setCities(all);
      setIsLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 150);
    return () => clearTimeout(timer);
  }, [query]);

  const filtered = useMemo(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) return [];
    const normalizedQuery = normalize(debouncedQuery);
    return cities
      .filter((c) => c.normalized.startsWith(normalizedQuery))
      .slice(0, 20);
  }, [debouncedQuery, cities]);

  return { suggestions: filtered, isLoading };
}
